import React from 'react';
import { ForgingSequence } from '../engineeringRules';

interface Props { seq: ForgingSequence; }

export default function SequenceVisualiser({ seq }: Props) {
  const isNut = seq.part.part_type === 'NUT';
  return isNut ? <NutVisualiser seq={seq} /> : <BoltVisualiser seq={seq} />;
}

/* ---- BOLT visualiser ---- */
function BoltVisualiser({ seq }: { seq: ForgingSequence }) {
  const wire = seq.wire_diameter_mm;
  const blank = seq.blank_length_mm;
  const d = seq.part.nominal_diameter_mm;
  const stations = seq.stations;

  // Each stage: cut-off + N stations
  const totalStages = stations.length + 1;
  const svgW = 680;
  const svgH = 140;
  const cy = 70;
  const stageW = Math.floor((svgW - 40) / totalStages);

  // Scale wire to fit nicely in height
  const wireScale = Math.min(2.5, 40 / wire);
  const wireH = wire * wireScale;
  const blankW = Math.min(blank * wireScale, stageW - 16);

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
      <defs>
        <marker id="bv-arr" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M1 1L7 4L1 7" fill="none" stroke="#3a4560" strokeWidth="1.5" strokeLinecap="round"/>
        </marker>
      </defs>

      {/* Stage 0: cut-off blank */}
      {(() => {
        const x = 20;
        return (
          <g key="cutoff">
            <rect x={x} y={cy - wireH / 2} width={blankW} height={wireH} rx="2"
              fill="rgba(74,158,255,0.18)" stroke="rgba(74,158,255,0.6)" strokeWidth="0.8"/>
            <text x={x + blankW / 2} y={cy + wireH / 2 + 14}
              textAnchor="middle" fill="#5a6070" fontSize="8.5" fontFamily="'DM Mono',monospace">
              CUT-OFF
            </text>
            <text x={x + blankW / 2} y={cy + wireH / 2 + 25}
              textAnchor="middle" fill="#5a6070" fontSize="8" fontFamily="'DM Mono',monospace">
              Ø{wire.toFixed(2)}×{blank.toFixed(1)}
            </text>
            <line x1={x + blankW + 3} y1={cy} x2={x + stageW - 6} y2={cy}
              stroke="#3a4560" strokeWidth="0.8" markerEnd="url(#bv-arr)"/>
          </g>
        );
      })()}

      {/* Stations */}
      {stations.map((stn, i) => {
        const x = 20 + (i + 1) * stageW;
        const progress = (i + 1) / stations.length;
        // Head grows, shank stays
        const headH = wireH * (1 + progress * 0.9);
        const headW = Math.min(d * wireScale * 1.4, stageW * 0.35);
        const shankW = blankW - headW;
        const shankH = wireH * (1 - progress * 0.08);
        const isLast = i === stations.length - 1;
        const accent = isLast ? '#3dcc8e' : '#e87c2e';
        const accentDim = isLast ? 'rgba(61,204,142,0.15)' : 'rgba(232,124,46,0.15)';

        return (
          <g key={stn.station}>
            {/* Head */}
            <rect x={x} y={cy - headH / 2} width={headW} height={headH} rx="2"
              fill={accentDim} stroke={accent} strokeWidth="0.8"/>
            {/* Shank */}
            <rect x={x + headW} y={cy - shankH / 2} width={Math.min(shankW, stageW - headW - 16)} height={shankH} rx="1"
              fill="rgba(232,124,46,0.08)" stroke="rgba(232,124,46,0.3)" strokeWidth="0.5"/>
            {/* Label */}
            <text x={x + headW / 2} y={cy + headH / 2 + 14}
              textAnchor="middle" fill="#9aa0b0" fontSize="8.5" fontFamily="'DM Mono',monospace">
              STN {stn.station}
            </text>
            <text x={x + headW / 2} y={cy + headH / 2 + 24}
              textAnchor="middle" fill="#5a6070" fontSize="7.5" fontFamily="'DM Mono',monospace">
              {stn.pallet_material !== 'NONE' ? stn.pallet_material : stn.punch_insert_material}
            </text>
            {/* Arrow to next */}
            {!isLast && (
              <line x1={x + Math.min(shankW, stageW - headW - 16) + headW + 4} y1={cy}
                x2={x + stageW - 6} y2={cy}
                stroke="#3a4560" strokeWidth="0.8" markerEnd="url(#bv-arr)"/>
            )}
          </g>
        );
      })}

      {/* Wire label top-left */}
      <text x={20} y={14} fill="#5a6070" fontSize="9" fontFamily="'DM Mono',monospace">
        Template {seq.template} · L/D {seq.ld_ratio.toFixed(2)} · {seq.wire_diameter_mm.toFixed(2)}mm wire
      </text>
    </svg>
  );
}

/* ---- NUT visualiser ---- */
function NutVisualiser({ seq }: { seq: ForgingSequence }) {
  const wire = seq.wire_diameter_mm;
  const d = seq.part.nominal_diameter_mm;
  const af = seq.part.nut_af_mm ?? d * 1.7;
  const nutH = seq.part.nut_height_mm ?? d * 0.8;
  const stations = seq.stations;
  const totalStages = stations.length + 1;

  const svgW = 680;
  const svgH = 160;
  const cy = 80;
  const stageW = Math.floor((svgW - 40) / totalStages);

  const scale = Math.min(3.2, 55 / af);
  const wireD = wire * scale;
  const finalAF = af * scale;
  const finalH = nutH * scale;

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
      <defs>
        <marker id="nv-arr" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M1 1L7 4L1 7" fill="none" stroke="#3a4560" strokeWidth="1.5" strokeLinecap="round"/>
        </marker>
      </defs>

      {/* Stage 0: wire cross-section (circle) */}
      {(() => {
        const x = 20 + stageW / 2;
        return (
          <g key="wire">
            <circle cx={x} cy={cy} r={wireD / 2}
              fill="rgba(74,158,255,0.18)" stroke="rgba(74,158,255,0.6)" strokeWidth="0.8"/>
            <text x={x} y={cy + wireD / 2 + 14}
              textAnchor="middle" fill="#5a6070" fontSize="8.5" fontFamily="'DM Mono',monospace">
              BLANK
            </text>
            <text x={x} y={cy + wireD / 2 + 24}
              textAnchor="middle" fill="#5a6070" fontSize="8" fontFamily="'DM Mono',monospace">
              Ø{wire.toFixed(2)}mm
            </text>
            <line x1={x + wireD / 2 + 3} y1={cy} x2={20 + stageW - 6} y2={cy}
              stroke="#3a4560" strokeWidth="0.8" markerEnd="url(#nv-arr)"/>
          </g>
        );
      })()}

      {/* Stations */}
      {stations.map((stn, i) => {
        const x = 20 + (i + 1) * stageW;
        const cx2 = x + stageW / 2;
        const progress = (i + 1) / stations.length;

        // Interpolate from wire to final dimensions
        const stageAF = wireD + (finalAF - wireD) * Math.min(progress * 1.15, 1);
        const stageH = Math.max(wireD * 0.6, finalH * (0.55 + progress * 0.45));

        const isHex = (seq.template === 'D2' && i >= 1) ||
                      (seq.template !== 'D2' && i >= 2);
        const isActive = stn.ko_type === 'ACTIVE';
        const isLast = i === stations.length - 1;

        const fillColor = isActive ? 'rgba(61,204,142,0.15)' : 'rgba(232,124,46,0.13)';
        const strokeColor = isActive ? '#3dcc8e' : '#e87c2e';

        return (
          <g key={stn.station}>
            {/* Nut profile */}
            <rect
              x={cx2 - stageAF / 2}
              y={cy - stageH / 2}
              width={stageAF}
              height={stageH}
              rx={isHex ? 2 : stageAF / 2}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={isLast ? 1.2 : 0.8}
            />
            {/* Hex marker */}
            {isHex && (
              <text x={cx2} y={cy + 3}
                textAnchor="middle" fill={strokeColor} fontSize="7" fontFamily="'DM Mono',monospace"
                opacity="0.7">
                HEX
              </text>
            )}
            {/* Active KO indicator */}
            {isActive && (
              <text x={cx2} y={cy - stageH / 2 - 6}
                textAnchor="middle" fill="#3dcc8e" fontSize="7.5" fontFamily="'DM Mono',monospace">
                ▲ KO
              </text>
            )}
            {/* Station 6 pierce */}
            {stn.station === 6 && (
              <circle cx={cx2} cy={cy} r={stageAF * 0.2}
                fill="var(--forge-bg, #0a0b0d)" stroke="#e87c2e" strokeWidth="0.6" strokeDasharray="2 1"/>
            )}
            {/* Labels */}
            <text x={cx2} y={cy + stageH / 2 + 14}
              textAnchor="middle" fill="#9aa0b0" fontSize="8.5" fontFamily="'DM Mono',monospace">
              STN {stn.station}
            </text>
            <text x={cx2} y={cy + stageH / 2 + 24}
              textAnchor="middle" fill="#5a6070" fontSize="7.5" fontFamily="'DM Mono',monospace">
              {stn.station === 6 ? 'PIERCE' : isHex ? `A/F ${(stageAF / scale).toFixed(1)}` : 'ROUND'}
            </text>
            {/* Arrow */}
            {!isLast && (
              <line x1={cx2 + stageAF / 2 + 3} y1={cy}
                x2={x + stageW - 6} y2={cy}
                stroke="#3a4560" strokeWidth="0.8" markerEnd="url(#nv-arr)"/>
            )}
          </g>
        );
      })}

      {/* Header */}
      <text x={20} y={14} fill="#5a6070" fontSize="9" fontFamily="'DM Mono',monospace">
        Template {seq.template} · {d}mm nut · Target A/F {af.toFixed(1)}mm · Height {nutH.toFixed(1)}mm
      </text>
    </svg>
  );
}
